<template>
  <section class="cn-about-gaming-section">
    <div class="container">
      <div class="cn-about-gaming-section__text">
        <div class="cn-about-gaming-section__text-left">
          <h2 class="cn-about-gaming-section__title">
            {{ data.title }}
          </h2>
          <UiLink v-if="data.link" :to="data.link.url">
            <span>{{ data.link.text }}</span>
            <UiIcon name="arrow-right" />
          </UiLink>
        </div>
        <div class="cn-about-gaming-section__text-right">
          <p class="cn-about-gaming-section__description">
            {{ data.description }}
          </p>
        </div>
      </div>
      <div class="cn-about-gaming-section__content">
        <div class="cn-about-gaming-section__icon">
          <UiIconYouCan />
        </div>
        <div class="cn-about-gaming-section__image">
          <CdnImg
            v-if="data.image"
            :src="data.image.src !== undefined ? data.image.src : ''"
            format="webp"
            :width="1044"
            :height="540"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';
import UiIconYouCan from '@/components/ui/icons/UiIconYouCan';

interface Props {
  data: {
    title?: string;
    description?: string;
    image?: {
      src?: string;
    };
    link?: {
      url?: string;
      text?: string;
    };
  };
}

defineProps<Props>();
</script>

<style lang="scss">
.cn-about-gaming-section {
  --ab-canyon-about-gaming-section-title-color-text: var(
    --ab-canyon-color-primary
  );
  --ab-canyon-about-gaming-section-text-left-links-color-text: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-about-gaming-section-description-color-text: var(
    --ab-canyon-color-neutral-dark
  );

  padding-top: calc(var(--size-base) * 4);
  padding-bottom: calc(var(--size-base) * 4);
  &__text {
    row-gap: var(--size-base);
    column-gap: calc(var(--size-base) * 4);
    justify-content: space-between;
    flex-direction: column;
    display: flex;
    @media (min-width: 656px) {
      flex-direction: row;
    }
  }
  &__text-left {
    a {
      color: var(--ab-canyon-about-gaming-section-text-left-links-color-text);
      column-gap: calc(var(--size-base) / 4);
      align-items: center;
      display: flex;
      margin-top: var(--size-base);
      &:hover {
        .cn-icon {
          transform: translateX(calc(var(--size-base) / 4));
        }
      }
      .cn-icon {
        transition-property: transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
        flex-shrink: 0;
        width: var(--size-base);
        height: var(--size-base);
      }
    }
  }
  &__title {
    color: var(--ab-canyon-about-gaming-section-title-color-text);
    text-transform: uppercase;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.25;

    @media (min-width: 992px) {
      font-size: 2.25rem;
      line-height: 2.5rem;
    }

    @media (min-width: 1280px) {
      line-height: 1.25;
    }

    @media (min-width: 1656px) {
      font-size: 3rem;
      line-height: 1;
    }
  }

  &__text-right {
    max-width: 570px;
  }
  &__description {
    color: var(--ab-canyon-about-gaming-section-description-color-text);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  &__content {
    column-gap: calc(var(--size-base) / 2);
    align-items: center;
    display: flex;
    margin-top: calc(var(--size-base) * 4);
    @media (min-width: 656px) {
      column-gap: var(--size-3xl);
    }
    @media (min-width: 1280px) {
      column-gap: var(--size-7xl);
    }
  }
  &__icon {
    width: 33.333333%;
    svg {
      color: var(--ab-canyon-about-gaming-section-title-color-text);
      width: 100%;
    }
  }
  &__image {
    width: 66.666667%;
    img {
      object-fit: cover;
      width: 100%;
      height: calc(var(--size-base) * 16);
      @media (min-width: 656px) {
        height: auto;
      }
    }
  }
}
</style>
